/* eslint-disable */
import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  FormControlLabel,
  Switch,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import MenuItem from "@mui/material/MenuItem";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import JoditEditor from "jodit-react";
import { useLazyQuery, useMutation } from "@apollo/client";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { SCHEDULE_POST } from "../../gql/mutations/scrapy";
import { GENERATE_BLOG, GENERATE_PARAGRAPH, GENERATE_HEADING } from "../../gql/mutations/keyword";
import { GetProjectByUserId } from "../../hooks/userWebsiteData";
import { usePopup } from "../../context/commonPop";
import { GET_WORDPRESS_CATEGORIES } from "../../gql/queries/scrapy";
import { convertToBase64 } from "../../utils/myUtills";
import ImageCropper from "../common/ImageCropper";
import FeaturedImageEditor from "../common/FeaturedImageEditor";

const STEPS = ["Content", "Select Website", "Schedule & Confirm"];

const PLATFORM_COLORS = {
  wordpress: "#21759b",
  shopify: "#95bf47",
  magento: "#f46f25",
};

const PLATFORM_LABELS = {
  wordpress: "WordPress",
  shopify: "Shopify",
  magento: "Magento",
};

export default function SchedulePostDialog({ open, onClose, onSuccess }) {
  const theme = useTheme();
  const { handleOpen } = usePopup();

  const [activeStep, setActiveStep] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [description, setDescription] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [scheduledAt, setScheduledAt] = useState(dayjs().add(1, "hour"));
  const [aiGenerating, setAiGenerating] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [useCustomImage, setUseCustomImage] = useState(false);
  const [featuredImageUrl, setFeaturedImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [croppedForEditor, setCroppedForEditor] = useState(null);

  const { allProjectWithSessionId: projects, loading: projectsLoading } = GetProjectByUserId({
    RequireSessionId: 1,
  });

  const [schedulePost, { loading: scheduling }] = useMutation(SCHEDULE_POST);
  const [generateBlog] = useMutation(GENERATE_BLOG);
  const [generateParagraph] = useMutation(GENERATE_PARAGRAPH);
  const [generateHeading] = useMutation(GENERATE_HEADING);
  const [fetchCategories, { loading: categoriesLoading }] = useLazyQuery(GET_WORDPRESS_CATEGORIES, {
    fetchPolicy: "network-only",
    onCompleted: (data) => setCategories(data?.getWordPressCategories || []),
  });

  const joditConfig = useMemo(
    () => ({
      readonly: false,
      height: 360,
      toolbar: true,
      toolbarAdaptive: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      removeButtons: ["about", "file", "video", "classSpan"],
      askBeforePasteHTML: false,
      defaultActionOnPaste: "insert_only_text",
    }),
    []
  );

  const handleBodyChange = useCallback((newContent) => {
    setBody(newContent);
  }, []);

  const handleGenerateWithAI = async () => {
    if (!title.trim()) {
      handleOpen(false, "Please enter a blog title first");
      return;
    }
    setAiGenerating(true);
    try {
      const trimmedTitle = title.trim();

      // Step 1: Generate introduction
      const { data: paraData } = await generateParagraph({
        variables: { input: { count: 1, title: trimmedTitle } },
      });
      const introduction = paraData?.generateParagraph?.[0]?.Introduction || "";

      // Step 2: Generate headings
      const { data: headingData } = await generateHeading({
        variables: { input: { count: 1, title: trimmedTitle } },
      });
      const heading_set = headingData?.generateHeading?.heading_sets?.[0] || [];

      // Step 3: Generate full blog with all 3 inputs
      const { data } = await generateBlog({
        variables: {
          input: {
            title: trimmedTitle,
            introduction,
            heading_set,
          },
        },
      });
      if (data?.generateAiBlog?.content) {
        setBody(data.generateAiBlog.content);
        handleOpen(true, "Blog generated successfully");
      }
    } catch (e) {
      handleOpen(false, e.message || "Failed to generate blog");
    } finally {
      setAiGenerating(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!title.trim()) {
        handleOpen(false, "Please enter a blog title");
        return;
      }
      if (!body.trim() || body === "<p><br></p>") {
        handleOpen(false, "Please enter blog content");
        return;
      }
    }
    if (activeStep === 1 && !selectedProject) {
      handleOpen(false, "Please select a website");
      return;
    }
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const handleSubmit = async () => {
    if (!scheduledAt || !scheduledAt.isValid()) {
      handleOpen(false, "Please select a valid date and time");
      return;
    }
    if (scheduledAt.isBefore(dayjs())) {
      handleOpen(false, "Scheduled time must be in the future");
      return;
    }
    try {
      await schedulePost({
        variables: {
          input: {
            projectId: selectedProject.id || selectedProject._id,
            title: title.trim(),
            body,
            description: description.trim() || undefined,
            publishAt: scheduledAt.toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(selectedCategoryId && { categoryId: selectedCategoryId }),
            ...(useCustomImage && featuredImageUrl.trim() && { featuredImageUrl: featuredImageUrl.trim() }),
          },
        },
      });
      handleOpen(true, "Post scheduled successfully");
      handleReset();
      onSuccess?.();
    } catch (e) {
      handleOpen(false, e.message || "Failed to schedule post");
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setTitle("");
    setBody("");
    setDescription("");
    setSelectedProject(null);
    setScheduledAt(dayjs().add(1, "hour"));
    setSelectedCategoryId("");
    setCategories([]);
    setUseCustomImage(false);
    setFeaturedImageUrl("");
    setImagePreview(null);
    setCropperOpen(false);
    setRawImageSrc(null);
    setEditorOpen(false);
    setCroppedForEditor(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", minHeight: "580px" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            Schedule a Blog Post
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Step 0 — Content */}
        {activeStep === 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={1} alignItems="flex-start">
              <TextField
                label="Blog Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                placeholder="e.g. 10 Tips for Better SEO in 2025"
                inputProps={{ maxLength: 200 }}
              />
              <Tooltip title="Generate blog content with AI using this title">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={
                      aiGenerating ? (
                        <CircularProgress size={16} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    onClick={handleGenerateWithAI}
                    disabled={aiGenerating || !title.trim()}
                    sx={{
                      minWidth: 160,
                      height: 56,
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {aiGenerating ? "Generating…" : "Generate with AI"}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                Blog Content <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <JoditEditor value={body} config={joditConfig} onBlur={handleBodyChange} />
            </Box>

            <TextField
              label="Meta Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Brief summary of the blog post for SEO"
              inputProps={{ maxLength: 320 }}
              helperText={`${description.length}/320 characters`}
            />
          </Box>
        )}

        {/* Step 1 — Select Website */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Choose the website you want to publish this post on.
            </Typography>
            {projectsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : projects.length === 0 ? (
              <Box textAlign="center" py={4}>
                <LanguageIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                <Typography color="text.secondary">No connected websites found.</Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {projects.map((project) => {
                  const isSelected = selectedProject
                    ? (selectedProject.id && selectedProject.id === project.id) ||
                      (selectedProject._id && selectedProject._id === project._id)
                    : false;
                  const appType = (project.appType || "").toLowerCase();
                  const platformColor = PLATFORM_COLORS[appType] || theme.palette.primary.main;
                  const platformLabel = PLATFORM_LABELS[appType] || project.appType || "Platform";
                  return (
                    <Grid item xs={12} sm={6} key={project.id || project._id}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: "10px",
                          borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
                          borderWidth: isSelected ? 2 : 1,
                          transition: "all 0.15s",
                          position: "relative",
                          boxShadow: isSelected
                            ? `0 0 0 2px ${theme.palette.primary.main}22`
                            : "none",
                        }}
                      >
                        <CardActionArea onClick={() => {
                          setSelectedProject(project);
                          setSelectedCategoryId("");
                          setCategories([]);
                          const projectId = project.id || project._id;
                          if (projectId) {
                            fetchCategories({ variables: { projectId } });
                          }
                        }} sx={{ p: 0 }}>
                          <CardContent sx={{ p: "14px 16px !important" }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                              <Box flex={1} minWidth={0}>
                                <Typography variant="subtitle2" fontWeight={600} noWrap>
                                  {project.projectName || project.website}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  display="block"
                                >
                                  {project.website}
                                </Typography>
                              </Box>
                              {isSelected && (
                                <CheckCircleIcon
                                  sx={{ color: theme.palette.primary.main, ml: 1, fontSize: 22 }}
                                />
                              )}
                            </Box>
                            <Box mt={1}>
                              <Chip
                                label={platformLabel}
                                size="small"
                                sx={{
                                  backgroundColor: platformColor + "20",
                                  color: platformColor,
                                  fontWeight: 600,
                                  fontSize: "0.72rem",
                                  height: 22,
                                }}
                              />
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        {/* Step 2 — Schedule */}
        {activeStep === 2 && (
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Summary */}
            <Box
              sx={{
                backgroundColor: theme.palette.background.default,
                borderRadius: "10px",
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600} mb={1} display="block">
                POST SUMMARY
              </Typography>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {title}
              </Typography>
              <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                <LanguageIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {selectedProject?.projectName || selectedProject?.website}
                </Typography>
                {selectedProject?.appType && (
                  <Chip
                    label={PLATFORM_LABELS[(selectedProject.appType || "").toLowerCase()] || selectedProject.appType}
                    size="small"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                )}
              </Box>
            </Box>

            {/* Category Selector */}
            {categories.length > 0 && (
              <Box>
                <Typography variant="body2" fontWeight={600} mb={1.5}>
                  Select Category
                </Typography>
                <TextField
                  select
                  fullWidth
                  label="Category"
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  helperText="Optional — defaults to Uncategorized"
                >
                  <MenuItem value="">
                    <em>None (Uncategorized)</em>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            {categoriesLoading && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading categories...</Typography>
              </Box>
            )}

            {/* Featured Image */}
            <Box>
              <FormControlLabel
                control={<Switch checked={useCustomImage} onChange={(e) => { setUseCustomImage(e.target.checked); if (!e.target.checked) { setFeaturedImageUrl(""); setImagePreview(null); setRawImageSrc(null); } }} />}
                label="Upload custom featured image"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mt: -0.5 }}>
                {useCustomImage ? "Select an image and crop to 1400×840" : "The first image from blog content will be used automatically"}
              </Typography>
              {useCustomImage && (
                <Box sx={{ mt: 1 }}>
                  <Button variant="outlined" component="label" sx={{ textTransform: "none" }}>
                    {imagePreview ? "Change Image" : "Choose Image"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const base64 = await convertToBase64(file);
                          setRawImageSrc(base64);
                          setCropperOpen(true);
                        }
                        e.target.value = "";
                      }}
                    />
                  </Button>
                  {imagePreview && (
                    <Box sx={{ mt: 1, borderRadius: "8px", overflow: "hidden", maxHeight: 150 }}>
                      <img src={imagePreview} alt="Preview" style={{ width: "100%", maxHeight: 150, objectFit: "cover", borderRadius: "8px" }} />
                    </Box>
                  )}
                </Box>
              )}
              <ImageCropper
                open={cropperOpen}
                imageSrc={rawImageSrc}
                onClose={() => setCropperOpen(false)}
                onCropDone={(croppedBase64) => {
                  setCroppedForEditor(croppedBase64);
                  setCropperOpen(false);
                  setEditorOpen(true);
                }}
              />
              <FeaturedImageEditor
                open={editorOpen}
                backgroundSrc={croppedForEditor}
                blogTitle={title}
                onClose={() => setEditorOpen(false)}
                onDone={(finalBase64) => {
                  setFeaturedImageUrl(finalBase64);
                  setImagePreview(finalBase64);
                  setEditorOpen(false);
                }}
              />
            </Box>

            {/* Date & Time Picker */}
            <Box>
              <Typography variant="body2" fontWeight={600} mb={1.5}>
                Select Date & Time
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Publish Date & Time"
                  value={scheduledAt}
                  onChange={(newValue) => setScheduledAt(newValue)}
                  disablePast
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: `Your timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            {scheduledAt && scheduledAt.isValid() && scheduledAt.isAfter(dayjs()) && (
              <Box
                sx={{
                  backgroundColor: theme.palette.primary.light + "15",
                  borderRadius: "8px",
                  p: 1.5,
                  border: `1px solid ${theme.palette.primary.light}`,
                }}
              >
                <Typography variant="body2" color="primary.main" fontWeight={500}>
                  Will publish on {scheduledAt.format("dddd, MMMM D, YYYY [at] h:mm A")}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Box flex={1} />
        {activeStep > 0 && (
          <Button variant="outlined" onClick={handleBack} sx={{ textTransform: "none" }}>
            Back
          </Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", minWidth: 100 }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={scheduling}
            startIcon={scheduling ? <CircularProgress size={16} /> : null}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", minWidth: 140 }}
          >
            {scheduling ? "Scheduling…" : "Schedule Post"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
